import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { DeckType } from "src/api/flashcardDecks";

import { DefaultApp } from "../DefaultApp";
import { Flashcard, type FlashcardShortcut } from "../Flashcard";
import { ViewController } from "../ViewController";

interface ViewFlashcard {
	flashcard: { id: number; front: string; back: string };
}

interface Deck {
	id: number;
	name: string;
	type: DeckType;
	flashcards: ViewFlashcard[];
}

interface ViewAppProps {
	deck: Deck;
}

export function ViewApp({ deck }: ViewAppProps) {
	const { t } = useTranslation();
	const [viewIndex, setViewIndex] = useState(0);
	const [navStep, setNavStep] = useState(0);

	const viewTotal = deck.flashcards.length;
	const viewCurrent =
		viewTotal > 0 ? deck.flashcards[viewIndex % viewTotal] : null;

	const goNext = () => {
		if (viewTotal === 0) return;
		setViewIndex((i) => (i + 1) % viewTotal);
		setNavStep((s) => s + 1);
	};

	const goPrev = () => {
		if (viewTotal === 0) return;
		setViewIndex((i) => (i - 1 + viewTotal) % viewTotal);
		setNavStep((s) => s + 1);
	};

	const buildShortcuts = (): FlashcardShortcut[] => {
		if (viewTotal === 0) return [];
		return [
			{
				key: "ArrowLeft",
				direction: "left",
				color: "blue",
				label: t("decks.view.previous", "Poprzednia"),
				action: goPrev,
			},
			{
				key: "ArrowRight",
				direction: "right",
				color: "blue",
				label: t("decks.view.next", "Następna"),
				action: goNext,
			},
		];
	};

	const renderFlashcard = () => {
		if (!viewCurrent) return null;
		return (
			<Flashcard
				key={`view-${viewCurrent.flashcard.id}-${viewIndex}-${navStep}`}
				flashcard={viewCurrent.flashcard}
				renderController={(animateExit) => (
					<ViewController
						animateExit={animateExit}
						onPrevious={goPrev}
						onNext={goNext}
					/>
				)}
				shortcuts={buildShortcuts()}
			/>
		);
	};

	return (
		<DefaultApp
			deck={deck}
			deckId={deck.id}
			progress={{ current: viewIndex + 1, total: viewTotal }}
			currentMode={deck.type}
			renderFlashcard={renderFlashcard}
		/>
	);
}
